// D2-3 — REFUNDS, ON A MEMBER'S DETAIL VIEW.
//
// Their real Stripe payment history, and a refund action per payment. Its own file for the same
// reason the grants tab has one: App.tsx is at the R8 ceiling and an owner feature pays for its own
// lines rather than borrowing against them.
//
// TWO THINGS THIS COMPONENT DELIBERATELY DOES NOT DO, because they are the two ways a refund screen
// goes wrong:
//   1. IT NEVER COMPOSES THE CONFIRM SENTENCE. The server re-reads the charge from Stripe and
//      composes it from what Stripe just said, so the sentence can never describe a stale amount —
//      i.e. a different payment from the one about to be refunded.
//   2. IT NEVER DECIDES WHAT HAPPENS TO ACCESS. Which options exist for a given payment comes down
//      with the payment (`accessActions`), out of the shared law in src/refunds.js. The dashboard
//      cannot offer a choice the server would refuse, because it is not the one holding the list.
//
// R77 — **WHAT THIS SCREEN SAID WHEN IT COULD NOT READ ANYTHING, ON THE SCREEN THAT MOVES MONEY.**
// R76's derived roster named this file in the second it was written and could not audit it. Three
// things were wrong and the server was honest about all three (measured: both routes have a `try`
// and answer `ok: false` with a sentence, so NOTHING was changed on the server this round).
//
//   1. **"No payments on record." OVER A READ THAT NEVER LANDED.** The empty state was guarded on
//      `!data?.note` — the PAYLOAD's own note, which is the server's two honest 200s ("this member
//      has never paid for anything", "their payments could not be read from Stripe just now"). The
//      transport's failure was not in that guard at all, so a refused or dropped load drew the
//      sentence that says this member has never given her a cent. R60/R64's finding — the failure
//      state is not a MISSING sentence but a WRONG one — on the screen she refunds from.
//   2. **ONE `note` STATE DOING TWO JOBS** (R66). A failed re-read after a refund LANDED overwrote
//      the server's own account of what that refund did — the one sentence saying whether the money
//      went back and whether the access change applied — with "their payments could not be read".
//   3. **THE AMOUNT BOX**, which is its own law now: see `refundAmountFromInput` in src/refunds.js.
//
// And the two-post confirm went onto the shared transport (`postOwnerAction` / `postOwnerConfirmed`),
// which needed a fifth answer kind to carry it — R47's count-the-copies: this block was typed here
// and byte-for-byte again in `OwnerGrants`, and the two copies had already drifted.
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote, postOwnerAction, postOwnerConfirmed, ownerActionNote } from "../lib/owner-load";
import { refundAmountFromInput, REFUND_AMOUNT_UNREADABLE } from "../refunds.js";

type AccessOption = { key: string; label: string; sentence: string };

type Charge = {
  id: string;
  amountCents: number;
  amountRefundedCents: number;
  refundableCents: number;
  currency: string;
  description: string;
  createdAt: string | null;
  status: string;
  refunded: boolean;
  disputed: boolean;
  kind: "subscription" | "one-time" | "other";
  accessActions: AccessOption[];
  defaultAccessAction: string;
};

type Purchase = {
  productKey: string;
  amountCents: number | null;
  createdAt: string | null;
  revokedAt: string | null;
  revokedReason: string;
};

type Payments = {
  charges: Charge[];
  purchases: Purchase[];
  subscription: { tier: string; status: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean } | null;
  note?: string;
};

type Pending = { body: Record<string, unknown>; sentence: string };

// Display only. Every amount that MATTERS is an integer number of cents decided on the server; this
// exists so a row reads like money instead of like a number of cents.
const dollars = (cents: number, currency: string) =>
  `${currency?.toUpperCase() === "USD" ? "$" : ""}${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency?.toUpperCase() === "USD" ? "" : ` ${currency?.toUpperCase()}`}`;

export function OwnerRefunds({ memberId, onDenied }: { memberId: string; onDenied: () => void }) {
  const [data, setData] = useState<Payments | null>(null);
  const [loading, setLoading] = useState(false);
  // **TWO FACTS, TWO STATES (R66).** `loadNote` is what the READ said; `note` is what an ACT said.
  // One state doing both jobs means a failed re-read erases the only account of a refund that
  // already moved money, and proposing a refund erases the line saying the table under it is stale.
  const [loadNote, setLoadNote] = useState("");
  // The act's line carries WHETHER IT LANDED beside the words, because R66's ruling is that those
  // are two different facts and this house already paints them differently — the house gold for an
  // act that happened, `owner-fresh--failed` for one that did not. One state, so they cannot part.
  const [act, setAct] = useState<{ text: string; ok: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  // Per-charge choices, keyed by charge id so two rows cannot share one box.
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [actions, setActions] = useState<Record<string, string>>({});
  const [why, setWhy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const answer = await loadOwnerData<Payments>(`/api/owner/members/${encodeURIComponent(memberId)}/payments`);
    if (!answer.ok && answer.kind === "denied") { onDenied(); setLoading(false); return; }
    if (answer.ok) setData(answer.data);
    setLoadNote(ownerLoadNote(answer, "Their payments could not be read just now."));
    setLoading(false);
  }, [memberId, onDenied]);

  useEffect(() => { void load(); }, [load]);

  async function propose(charge: Charge) {
    // **THE AMOUNT IS READ BY A LAW, AND AN UNREADABLE ONE IS REFUSED RATHER THAN REPLACED (R44).**
    // `Math.round(Number("12o") * 100)` is NaN and `JSON.stringify` writes NaN as `null`, which is
    // this route's own word for "refund the whole of what is left". Nothing is posted.
    const amount = refundAmountFromInput(amounts[charge.id]);
    if (amount.kind === "unreadable") { setAct({ text: REFUND_AMOUNT_UNREADABLE, ok: false }); return; }
    const body = {
      chargeId: charge.id,
      amountCents: amount.cents,
      accessAction: actions[charge.id] || charge.defaultAccessAction,
      note: why,
    };
    setBusy(true);
    setAct(null);
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/refunds", body);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "confirm") { setPending({ body, sentence: answer.sentence }); return; }
    setAct({ text: ownerActionNote(answer, "That could not be prepared. Nothing was refunded."), ok: false });
  }

  async function commit() {
    if (!pending) return;
    setBusy(true);
    const answer = await postOwnerConfirmed<{ message?: string; sentence?: string }>("/api/owner/refunds", pending.body);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    // The server's own account of what it did, which on this route names BOTH acts — the money and
    // whether the access change applied. It is composed there and rendered here, never rewritten.
    setAct(answer.ok
      ? { text: String(answer.data?.message || "").trim() || "Refunded.", ok: true }
      : { text: ownerActionNote(answer, "That did not go through. No money was sent back."), ok: false });
    // **THE CONFIRM CLOSES ONLY ON A REFUND THAT LANDED, HELD BY POSITION (R61).** It used to be
    // dismissed on every answer, so a refusal from Stripe took away the exact sentence she had
    // approved and made her retype the amount to try again — and the thrown path left it open, so
    // the two failures did not even behave alike.
    if (answer.ok) { setPending(null); setWhy(""); setAmounts({}); void load(); }
  }

  return (
    <div className="owner-refunds">
      <h3 className="owner-detail__sub">Payments</h3>
      {loading ? <p className="owner-page__note">Loading...</p> : null}
      {data?.note ? <p className="owner-page__note">{data.note}</p> : null}
      {loadNote ? <p className="owner-page__note owner-fresh--failed" role="status">{loadNote}</p> : null}
      {act?.text ? <p className={act.ok ? "owner-act-note" : "owner-page__note owner-fresh--failed"} role="status">{act.text}</p> : null}

      {/* D2-4 — the confirm. The sentence is the SERVER'S, rendered verbatim, and it names both the
          money and what happens to their access. */}
      {pending ? (
        <div className="owner-confirm" role="alertdialog" aria-live="assertive">
          <p className="owner-confirm__sentence">{pending.sentence}</p>
          <div className="owner-confirm__actions">
            <button type="button" className="button" disabled={busy} onClick={() => { void commit(); }}>Confirm</button>
            <button type="button" className="owner-link" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {data?.charges?.length ? (
        <>
          <label className="owner-grant-form__field owner-grant-form__field--wide">
            <span>Why (kept in the log with every refund)</span>
            <input className="owner-search" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Optional" />
          </label>
          <div className="owner-table-wrap">
            <table className="owner-table">
              <thead>
                <tr><th>When</th><th>What</th><th>Amount</th><th>Refunded</th><th>Amount to refund</th><th>Their access</th><th /></tr>
              </thead>
              <tbody>
                {data.charges.map((c) => (
                  <tr key={c.id} data-charge={c.id} data-charge-kind={c.kind}>
                    {/* D6-M4 — each cell names its column, so the refund table becomes cards on
                        her phone: her ruling is that the refund flow is fully operable by thumb. */}
                    <td data-label="When">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</td>
                    <td data-label="What">
                      {c.description || (c.kind === "subscription" ? "Membership" : c.kind === "one-time" ? "One-time purchase" : "Payment")}
                      {c.disputed ? <div className="owner-catalog__note">Disputed at Stripe.</div> : null}
                    </td>
                    <td data-label="Amount">{dollars(c.amountCents, c.currency)}</td>
                    <td data-label="Refunded">{c.amountRefundedCents ? dollars(c.amountRefundedCents, c.currency) : "—"}</td>
                    <td data-label="To refund">
                      {c.refundableCents > 0 ? (
                        <input className="owner-search owner-search--tiny" type="number" step="0.01" min="0"
                          max={(c.refundableCents / 100).toFixed(2)}
                          aria-label={`Amount to refund of ${dollars(c.refundableCents, c.currency)}`}
                          placeholder={(c.refundableCents / 100).toFixed(2)}
                          value={amounts[c.id] ?? ""}
                          onChange={(e) => setAmounts((p) => ({ ...p, [c.id]: e.target.value }))} />
                      ) : "—"}
                    </td>
                    <td data-label="Their access">
                      {/* The options come DOWN with the payment. This list is never built here. */}
                      {c.refundableCents > 0 ? (
                        <select className="owner-search owner-search--tiny"
                          aria-label={`What happens to their access when this payment is refunded`}
                          value={actions[c.id] ?? c.defaultAccessAction}
                          onChange={(e) => setActions((p) => ({ ...p, [c.id]: e.target.value }))}>
                          {c.accessActions.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                        </select>
                      ) : "—"}
                    </td>
                    <td>
                      {c.refundableCents > 0 ? (
                        <button type="button" className="owner-link" disabled={busy} onClick={() => { void propose(c); }}>Refund</button>
                      ) : <span className="owner-page__note">Fully refunded</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (!loading && !loadNote && data && !data.note
        // **THE GUARD IS THE PAYLOAD'S PRESENCE (R53), NOT ITS TRUTHINESS.** It used to be
        // `!data?.note`, which is the SERVER'S own note field — so the transport's failure was in
        // no part of this condition and a read that never landed printed the sentence saying this
        // member has never given her a cent, on the screen she refunds from.
        ? <p className="owner-page__note">No payments on record.</p> : null)}

      {data?.purchases?.length ? (
        <>
          <h3 className="owner-detail__sub">One-time purchases</h3>
          <ul className="owner-bars">
            {data.purchases.map((p) => (
              <li key={p.productKey} data-purchase={p.productKey}>
                <span className="owner-bars__label">
                  {p.productKey}
                  {p.revokedAt ? ` · taken back ${new Date(p.revokedAt).toLocaleDateString()}${p.revokedReason ? ` (${p.revokedReason})` : ""}` : ""}
                </span>
                <span className="owner-bars__count">{p.amountCents ? dollars(p.amountCents, "usd") : "—"}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
